import { Loader2 } from "lucide-react";

/**
 * Full-screen loading overlay that blocks all user interaction.
 * Use for long-running operations (e.g. CAE generation, API calls) to prevent
 * the user from clicking elsewhere and causing unexpected issues.
 *
 * @example
 * ```tsx
 * {isProcessing && (
 *   <LoadingOverlay
 *     message="Obteniendo CAE de AFIP..."
 *     submessage="Por favor espere, no cierre esta ventana"
 *   />
 * )}
 * ```
 */
interface LoadingOverlayProps {
  /** Main message shown below the spinner */
  message?: string;
  /** Optional secondary hint (e.g. "Por favor espere...") */
  submessage?: string;
}

export function LoadingOverlay({
  message = "Procesando...",
  submessage,
}: LoadingOverlayProps) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm"
      aria-hidden="true"
      role="alert"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-4 rounded-lg bg-card p-8 shadow-lg border">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-sm font-medium">{message}</p>
        {submessage && (
          <p className="text-xs text-muted-foreground">{submessage}</p>
        )}
      </div>
    </div>
  );
}
